import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface MirrorInsight {
  id: string;
  highlight_text: string;
  badges: string[];
  recommended_action: string | null;
  recommended_action_route: string | null;
  generation_cost_usd: number | null;
  generated_at: string;
  seen_at: string | null;
  regenerated_from: string | null;
}

function getCurrentWeekStart(): string {
  var now = new Date();
  var day = now.getDay();
  var diff = day === 0 ? 6 : day - 1;
  var ws = new Date(now);
  ws.setDate(now.getDate() - diff);
  ws.setHours(0, 0, 0, 0);
  return ws.toISOString().split("T")[0];
}

export function useMirrorInsight(weekStart?: string) {
  var ws = weekStart || getCurrentWeekStart();
  var [insight, setInsight] = useState<MirrorInsight | null>(null);
  var [loading, setLoading] = useState(true);
  var [regenerating, setRegenerating] = useState(false);
  var [regenerationsUsed, setRegenerationsUsed] = useState(0);

  var fetchInsight = useCallback(async () => {
    try {
      var { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      var { data, error } = await (supabase as any)
        .from("mirror_insights")
        .select("id, highlight_text, badges, recommended_action, recommended_action_route, generation_cost_usd, generated_at, seen_at, regenerated_from")
        .eq("user_id", user.id)
        .eq("insight_type", "weekly")
        .eq("week_start", ws)
        .order("generated_at", { ascending: false })
        .limit(1);

      if (error) { console.error("[useMirrorInsight]", error); }

      var latest = data && data.length > 0 ? data[0] : null;
      setInsight(latest);

      // Mark as seen
      if (latest && !latest.seen_at) {
        (supabase as any).from("mirror_insights").update({ seen_at: new Date().toISOString() }).eq("id", latest.id).then(() => {});
      }

      // Count regenerations
      var { data: regenData } = await (supabase as any)
        .from("mirror_insights")
        .select("id")
        .eq("user_id", user.id)
        .eq("week_start", ws)
        .not("regenerated_from", "is", null);

      setRegenerationsUsed(regenData?.length || 0);
    } catch (e) {
      console.error("[useMirrorInsight]", e);
    }
    setLoading(false);
  }, [ws]);

  useEffect(() => { fetchInsight(); }, [fetchInsight]);

  var regenerate = useCallback(async () => {
    if (regenerationsUsed >= 2) return;
    setRegenerating(true);
    try {
      var { data: { user } } = await supabase.auth.getUser();
      if (!user) { setRegenerating(false); return; }

      var { data, error } = await supabase.functions.invoke("mirror-insights", {
        body: { user_id: user.id, trigger: "regenerate", week_start: ws },
      });

      if (error) {
        console.error("[useMirrorInsight] regenerate error:", error);
        setRegenerating(false);
        return { error: "failed" };
      }

      if (data?.error === "rate_limit") {
        setRegenerating(false);
        return { error: "rate_limit", retry_after_days: data.retry_after_days };
      }

      await fetchInsight();
      setRegenerating(false);
      return { success: true };
    } catch (e) {
      console.error("[useMirrorInsight] regenerate:", e);
      setRegenerating(false);
      return { error: "failed" };
    }
  }, [ws, regenerationsUsed, fetchInsight]);

  return {
    insight,
    loading,
    regenerating,
    regenerate,
    canRegenerate: regenerationsUsed < 2,
    regenerationsUsed,
  };
}
